/* ============================================================
   Modulo: Commerce Head — scroll behaviour + phone drawer.

   Everything else is CSS or native HTML on purpose: the category selector is
   a real <select>, the mega panels open on :hover/:focus-within, the drawer's
   sub-menus are <details>. This file only does the two things CSS cannot:
   decide when the head is stuck, and toggle the drawer.

   window.moduloLazyInit is deliberately NOT used — it does not exist on disk
   (modulo-core.js is absent) and calling it is what live-bugged
   section-stats-progress.
   ============================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var MQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  /* In the theme editor the page is short and the preview reloads constantly,
     so hiding the bar reads as a bug rather than a feature. Matches the copy
     in the setting's own info string. */
  function inEditor() { return !!(window.Shopify && window.Shopify.designMode); }

  function state(el) {
    if (!el.__hc) el.__hc = { lastY: window.scrollY || 0, ticking: false };
    return el.__hc;
  }

  /* The utility bar above may be sticky. When it is, the head has to dock
     BELOW it rather than at 0, or it covers the bar it should sit under. */
  function stuckTop(el) {
    var bar = document.querySelector('.modulo-ub--sticky');
    if (!bar) return 0;
    // Only a bar that actually sits above this header counts.
    if (bar.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) {
      return Math.round(bar.getBoundingClientRect().height);
    }
    return 0;
  }

  function measure(el) {
    el.style.setProperty('--modulo-hc-stuck-top', stuckTop(el) + 'px');
  }

  function apply(el) {
    var s = state(el);
    var y = window.scrollY || window.pageYOffset || 0;
    var mode = el.getAttribute('data-hc-behavior') || 'condense';

    if (mode === 'static') {
      el.classList.remove('is-stuck', 'is-hidden');
      s.lastY = y;
      return;
    }

    // The trip point is where the head's own top edge would leave the viewport.
    // offsetTop is read off the root, which stays in flow at the same place
    // whether or not the head is fixed, so this cannot oscillate.
    var trip = el.offsetTop - stuckTop(el);
    if (trip < 1) trip = 1;

    var stuck = y > trip;
    el.classList.toggle('is-stuck', stuck);

    if (mode === 'reveal' && !inEditor()) {
      // A 6px deadband — without it a 1px scroll jitter flickers the bar.
      if (Math.abs(y - s.lastY) > 6) {
        el.classList.toggle('is-hidden', stuck && y > s.lastY && y > trip + 120);
        s.lastY = y;
      }
    } else {
      el.classList.remove('is-hidden');
      s.lastY = y;
    }
  }

  function onScroll() {
    var els = document.querySelectorAll('[data-modulo-hc]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var s = state(el);
      if (s.ticking) continue;
      s.ticking = true;
      /* eslint-disable no-loop-func */
      (function (node, st) {
        window.requestAnimationFrame(function () {
          apply(node);
          st.ticking = false;
        });
      })(el, s);
      /* eslint-enable no-loop-func */
    }
  }

  function onResize() {
    var els = document.querySelectorAll('[data-modulo-hc]');
    for (var i = 0; i < els.length; i++) {
      measure(els[i]);
      apply(els[i]);
      // The drawer only exists below 860px; leaving it open past that point
      // would strand an invisible expanded state.
      if (window.innerWidth > 860) closeDrawer(els[i]);
    }
  }

  function closeDrawer(el) {
    el.classList.remove('is-open');
    var btn = el.querySelector('[data-hc-toggle]');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }

  function initSection(el) {
    measure(el);
    apply(el);

    var btn = el.querySelector('[data-hc-toggle]');
    if (btn && !btn.hasAttribute('data-hc-bound')) {
      btn.setAttribute('data-hc-bound', '');
      btn.addEventListener('click', function () {
        var open = el.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
  }

  function initAll() {
    var els = document.querySelectorAll('[data-modulo-hc]');
    for (var i = 0; i < els.length; i++) initSection(els[i]);
  }

  /* Document-level listeners are wired exactly once. An identical <script src>
     re-inserted by the theme editor still executes, so without this guard every
     section reload stacks another scroll handler (the `as` lesson). */
  if (!window.moduloHcWired) {
    window.moduloHcWired = true;

    window.addEventListener('scroll', onScroll, { passive: true });

    var rt = null;
    window.addEventListener('resize', function () {
      if (rt) clearTimeout(rt);
      rt = setTimeout(onResize, 120);
    });

    // Reduced motion changes nothing structural here, but a live listener keeps
    // the stuck state correct if the CSS transitions are swapped mid-session.
    if (MQ) {
      if (typeof MQ.addEventListener === 'function') MQ.addEventListener('change', onScroll);
      else if (typeof MQ.addListener === 'function') MQ.addListener(onScroll);
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var els = document.querySelectorAll('[data-modulo-hc].is-open');
      for (var i = 0; i < els.length; i++) closeDrawer(els[i]);
    });

    document.addEventListener('shopify:section:load', function (e) {
      var root = e.target && e.target.querySelector ? e.target.querySelector('[data-modulo-hc]') : null;
      if (root) initSection(root);
      else initAll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
